import React from 'react';
import { Box, ListItemButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { KeyCombo } from './KeyCap';
import { useShortcutsEnabled } from '../shortcutsEnabledStore';

/**
 * One row of a pedigree context menu.
 *
 * Every menu on the canvas renders through this — the single-person menu, the
 * two-selected Merge/Migrate menu, the multi-select menu and the proxy menu. They
 * used to be four sets of bare `ListItemButton`s, so styling the main one would
 * have left the other three looking like a different product (TGCLOUD-1644).
 *
 * The right-hand slot carries exactly one thing: a chevron when the row opens a
 * submenu, or a `hint` — "recorded", or the reason a row is unavailable. An
 * unavailable control always says why; that rule came from the sidebar's Add
 * Relative panel (TGCLOUD-1640) and this is the menu's half of it.
 *
 * `shortcut` is a separate slot, sitting between the label and that one, because a
 * row can legitimately need both a key cap and a chevron (Brother opens the twin
 * flyout AND answers to B). Two rules govern it:
 *
 * 1. **A reason beats a shortcut.** A row carrying a `hint` shows the hint and no
 *    cap — an unavailable row needs to say why far more than it needs to advertise
 *    a key that would do nothing, and the two together would contradict each other.
 * 2. **The caps follow the keyboard-shortcuts toggle** in the canvas toolbar. With
 *    shortcuts off, the keys genuinely do nothing, so advertising them would be
 *    wrong — and the clutter is unwanted by anyone who has turned them off, which
 *    is the whole reason the toggle exists.
 *
 * `variant="static"` is for a row that can NEVER become available — Parents once
 * parents exist. It renders as inert, unfocusable text rather than a disabled
 * button, because a disabled button that can never enable is a dead end for a
 * keyboard user and the reason belongs in the text rather than in a tooltip
 * nobody can reach. That is `RecordedChip`'s reasoning, applied to a menu row.
 * `disabled` is for the other case — Brother before parents exist — where the row
 * really can become available and should stay in the tab order.
 */

/**
 * Left icon gutter.
 *
 * Sized by the WIDEST icon, not the tallest: most glyphs are 18px square, but
 * Gender draws three symbols side by side (□ ○ ◇) and needs 32 to space them
 * without them running into each other. Browser QA caught them overlapping at the
 * original 26.
 */
export const ICON_SLOT = 32;

const MenuItemRow = ({
  icon = null,
  label,
  hint = '',
  shortcut = null,
  hasSubmenu = false,
  disabled = false,
  variant = 'action',
  tone,
  selected = false,
  accessibleName,
  onClick,
  onMouseEnter,
  ...rest
}) => {
  const shortcutsOn = useShortcutsEnabled();
  const name = hint ? `${accessibleName || label}, ${hint}` : accessibleName;

  const glyph = (
    <Box
      aria-hidden="true"
      sx={{
        width: ICON_SLOT,
        minWidth: ICON_SLOT,
        display: 'flex',
        alignItems: 'center',
        // The icon carries the group colour; the label stays neutral so twelve
        // rows do not become twelve colours of text.
        color: tone || 'inherit',
        // Dim only the glyph and the label when disabled — the hint must stay
        // readable, which is the whole point of showing it.
        opacity: disabled ? 0.45 : 1
      }}
    >
      {icon}
    </Box>
  );

  const text = (
    <Typography
      component="span"
      sx={{
        flex: 1,
        minWidth: 0,
        // 15px: 14 was chosen only to be smaller than the 16px default it
        // replaced, and browser QA found it a touch mean. The icon gutter and row
        // padding are unchanged, so the spacing either side of the glyph holds.
        fontSize: 15,
        lineHeight: 1.4,
        opacity: disabled || variant === 'static' ? 0.55 : 1
      }}
    >
      {label}
    </Typography>
  );

  // A reason beats a shortcut, and both defer to the toggle — see the header.
  const keys =
    shortcut && shortcutsOn && !hint && !disabled ? (
      <Box sx={{ ml: 1, display: 'inline-flex', flexShrink: 0 }}>
        <KeyCombo keys={shortcut} size="sm" />
      </Box>
    ) : null;

  const right = hasSubmenu ? (
    <ChevronRightIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
  ) : hint ? (
    <Typography
      component="span"
      sx={{
        ml: 1.5,
        // Tracks the label up, staying clearly secondary to it.
        fontSize: 13,
        color: 'text.secondary',
        whiteSpace: 'nowrap'
      }}
    >
      {hint}
    </Typography>
  ) : null;

  if (variant === 'static') {
    return (
      <Box
        aria-disabled="true"
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          color: 'text.primary'
        }}
        {...rest}
      >
        {glyph}
        {text}
        {keys}
        {right}
      </Box>
    );
  }

  return (
    <ListItemButton
      disabled={disabled}
      selected={selected}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      aria-label={name}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        ...(tone === 'error.main' && {
          color: 'error.main',
          '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.08) }
        }),
        // MUI dims the whole row at 0.38 when disabled, which would take the
        // reason down with it. Keep the row at full opacity and dim the glyph
        // and label individually (above) so the hint stays legible.
        '&.Mui-disabled': { opacity: 1 }
      }}
      {...rest}
    >
      {glyph}
      {text}
      {keys}
      {right}
    </ListItemButton>
  );
};

export default MenuItemRow;
